import { useState, useEffect } from 'react';
import { apiFetch } from '../shared/api.ts';
import { useAuth } from '../shared/AuthContext.tsx';
import { Cargando, ErrorApi } from '../shared/ui.tsx';
import { formatoRango } from '../shared/horas.ts';
import { QRCodeSVG } from 'qrcode.react';

type Salon = {
  id: string;
  nombre: string;
  latitud: number | null;
  longitud: number | null;
  radio_metros: number;
};

type QrData = {
  cliente_id: string;
  min: number;
  hmac: string;
};

type Convocatoria = {
  convocatoria_id: string;
  persona_nombre: string;
  persona_apellido: string;
  categoria_nombre: string;
  salon_nombre: string;
  hora_inicio: string;
  hora_fin: string;
};

type Checkin = {
  persona_nombre: string;
  persona_apellido: string;
  salon_nombre: string;
  checkin_hora: string;
};

type QrActual = {
  cliente_id: string;
  cliente_nombre: string;
  salones: Salon[];
  qr: QrData;
  convocatorias: Convocatoria[];
  checkins: Checkin[];
};

function Icono({ d, className = 'h-5 w-5' }: { d: string; className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

export function SalonesPage() {
  const { usuario } = useAuth();
  const clienteId = usuario?.cliente_id ?? '';

  const [qrActivo, setQrActivo] = useState(false);
  const [qrData, setQrData] = useState<QrActual | null>(null);
  const [cargandoQr, setCargandoQr] = useState(false);
  const [pinModal, setPinModal] = useState(false);
  const [pinValor, setPinValor] = useState('');
  const [pinError, setPinError] = useState('');
  const [verificando, setVerificando] = useState(false);
  const [pinVerificado, setPinVerificado] = useState('');
  const [segundosRestantes, setSegundosRestantes] = useState(60);

  useEffect(() => {
    if (!qrActivo || !clienteId) return;
    const interval = setInterval(() => {
      apiFetch<QrActual>(`/api/cliente/${clienteId}/qr-actual`)
        .then((data) => {
          setQrData(data);
          setSegundosRestantes(60);
        })
        .catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [qrActivo, clienteId]);

  useEffect(() => {
    if (!qrActivo) return;
    const timer = setInterval(() => {
      setSegundosRestantes(s => (s <= 1 ? 60 : s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [qrActivo]);

  async function verificarPin() {
    setVerificando(true);
    setPinError('');
    try {
      await apiFetch(`/api/cliente/${clienteId}/verificar-pin`, {
        method: 'POST',
        body: JSON.stringify({ pin: pinValor }),
      });
      setPinModal(false);
      setPinVerificado(pinValor);
      setPinValor('');
      await cargarQr();
    } catch (e) {
      setPinError(e instanceof Error ? e.message : 'PIN incorrecto');
    } finally {
      setVerificando(false);
    }
  }

  async function cargarQr() {
    setQrActivo(true);
    setCargandoQr(true);
    try {
      const data = await apiFetch<QrActual>(`/api/cliente/${clienteId}/qr-actual`);
      setQrData(data);
      setSegundosRestantes(60);
    } catch {
      setQrData(null);
    } finally {
      setCargandoQr(false);
    }
  }

  function toggleQr() {
    if (qrActivo) {
      setQrActivo(false);
      setQrData(null);
    } else {
      setPinModal(true);
      setPinValor('');
      setPinError('');
    }
  }

  if (!clienteId) return <ErrorApi mensaje="No se encontró el cliente" />;

  const qrPct = Math.round((segundosRestantes / 60) * 100);
  const qrExpirando = segundosRestantes <= 10;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-neutro-800">Código QR</h2>
        <p className="text-sm text-neutro-500 mt-0.5">
          Un solo código QR para todo el club. El personal lo escanea y la app detecta automáticamente en qué salón está.
        </p>
      </div>

      {/* QR Hero */}
      <div className="rounded-2xl border border-contorno-var bg-white overflow-hidden">
        <div className="bg-primario-400 px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-white">Código QR del club</p>
              <p className="text-sm text-white/70">{qrActivo ? 'Activo — se regenera cada minuto' : 'Tocá para activar'}</p>
            </div>
            <button
              onClick={toggleQr}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
            >
              <Icono d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0 1 3.75 9.375v-4.5ZM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 0 1-1.125-1.125v-4.5ZM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0 1 13.5 9.375v-4.5Z" className="h-5 w-5" />
            </button>
          </div>
        </div>

        {qrActivo && (
          <div className="p-5">
            {cargandoQr ? (
              <div className="py-8"><Cargando /></div>
            ) : qrData ? (
              <div className="flex flex-col items-center gap-4">
                {/* QR with scanner animation */}
                <div className="relative">
                  <div className="rounded-2xl bg-white p-4 shadow-sm border border-contorno-var">
                    <QRCodeSVG
                      value={JSON.stringify(qrData.qr)}
                      size={180}
                      level="M"
                      bgColor="#ffffff"
                      fgColor="#261813"
                    />
                  </div>
                  {/* Scanner line animation */}
                  <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
                    <div className="absolute inset-x-4 top-0 h-0.5 bg-primario-400 animate-[scan_2s_ease-in-out_infinite]" />
                  </div>
                </div>

                {/* Timer */}
                <div className="flex items-center gap-3">
                  <div className="relative h-10 w-10">
                    <svg className="h-10 w-10 -rotate-90" viewBox="0 0 40 40">
                      <circle cx="20" cy="20" r="17" fill="none" stroke="#fde3da" strokeWidth="3" />
                      <circle cx="20" cy="20" r="17" fill="none" stroke="#f26522" strokeWidth="3" strokeDasharray="106.8" strokeDashoffset={106.8 * (1 - qrPct / 100)} strokeLinecap="round" className="transition-all duration-1000" />
                    </svg>
                    <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${qrExpirando ? 'text-red-500 animate-pulse' : 'text-neutro-600'}`}>
                      {segundosRestantes}s
                    </span>
                  </div>
                  <span className="text-xs text-neutro-500">
                    {qrExpirando ? 'Expira pronto — esperá regeneración' : 'Se regenera automáticamente'}
                  </span>
                </div>

                <a
                  href={`/kiosk?cliente_id=${clienteId}&pin=${encodeURIComponent(pinVerificado)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primario-400 hover:text-primario-500 transition-colors"
                >
                  <Icono d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" className="h-4 w-4" />
                  Abrir kiosk en pantalla grande
                </a>
              </div>
            ) : (
              <p className="text-sm text-red-500 text-center py-4">No se pudo generar el QR</p>
            )}
          </div>
        )}
      </div>

      {/* Info cards */}
      {qrActivo && qrData && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Salones */}
          {qrData.salones.length > 0 && (
            <div className="rounded-2xl border border-contorno-var bg-white p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primario-100">
                  <Icono d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" className="h-4 w-4 text-primario-400" />
                </div>
                <h3 className="text-sm font-semibold text-neutro-800">Salones ({qrData.salones.length})</h3>
              </div>
              <ul className="space-y-2">
                {qrData.salones.map(sal => (
                  <li key={sal.id} className="flex items-center justify-between rounded-lg bg-superficie px-3 py-2.5 text-sm">
                    <span className="font-medium text-neutro-700">{sal.nombre}</span>
                    <span className="text-xs text-neutro-400">Radio: {sal.radio_metros}m</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Convocatorias de hoy */}
          {qrData.convocatorias.length > 0 && (
            <div className="rounded-2xl border border-contorno-var bg-white p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100">
                  <Icono d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" className="h-4 w-4 text-amber-500" />
                </div>
                <h3 className="text-sm font-semibold text-neutro-800">Esperados hoy ({qrData.convocatorias.length})</h3>
              </div>
              <ul className="space-y-2">
                {qrData.convocatorias.map(c => (
                  <li key={c.convocatoria_id} className="flex items-center justify-between rounded-lg bg-superficie px-3 py-2.5 text-sm">
                    <div className="min-w-0">
                      <span className="font-medium text-neutro-700">{c.persona_apellido} {c.persona_nombre}</span>
                      <span className="ml-2 text-xs text-neutro-400">{c.categoria_nombre}</span>
                    </div>
                    <span className="text-xs text-neutro-400 shrink-0 ml-2">{c.salon_nombre} · {formatoRango(c.hora_inicio, c.hora_fin)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Check-ins */}
      {qrActivo && qrData && qrData.checkins.length > 0 && (
        <div className="rounded-2xl border border-green-200 bg-green-50 p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-100">
              <Icono d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" className="h-4 w-4 text-estado-ok" />
            </div>
            <h3 className="text-sm font-semibold text-estado-ok">Check-ins realizados ({qrData.checkins.length})</h3>
          </div>
          <ul className="space-y-2">
            {qrData.checkins.map((ch, i) => (
              <li key={i} className="flex items-center justify-between rounded-lg bg-white border border-green-200 px-3 py-2.5 text-sm">
                <span className="font-medium text-neutro-700">{ch.persona_apellido} {ch.persona_nombre}</span>
                <span className="text-xs text-neutro-400">{ch.salon_nombre} · {new Date(ch.checkin_hora).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Modal PIN */}
      {pinModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl border border-contorno-var">
            <div className="mb-6 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primario-100">
                <Icono d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" className="h-6 w-6 text-primario-400" />
              </div>
              <h3 className="text-lg font-bold text-neutro-800">Ingresar PIN</h3>
              <p className="text-sm text-neutro-500 mt-1">Ingresá el PIN del club para ver el código QR.</p>
            </div>
            <input
              type="password"
              className="campo mb-4 text-center text-2xl tracking-[0.5em]"
              maxLength={6}
              placeholder="····"
              autoFocus
              value={pinValor}
              onChange={(e) => { setPinValor(e.target.value); setPinError(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && pinValor.length >= 4) verificarPin(); }}
            />
            {pinError && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2">
                <p className="text-sm text-red-600">{pinError}</p>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={verificarPin} disabled={verificando || pinValor.length < 4} className="flex-1 boton bg-primario-400 text-white hover:bg-primario-500 disabled:opacity-50">
                {verificando ? 'Verificando…' : 'Ver QR'}
              </button>
              <button onClick={() => { setPinModal(false); setPinValor(''); setPinError(''); }} className="boton border border-contorno-var bg-white text-neutro-600 hover:bg-superficie-100">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scanner animation keyframes */}
      <style>{`
        @keyframes scan {
          0%, 100% { transform: translateY(0); opacity: 0.5; }
          50% { transform: translateY(180px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
